"use client"

import { useEffect, useState } from "react"
import { getDatabase, ref, get, update, push, onValue } from "firebase/database"
import { getAuth } from "firebase/auth"
import { format, toZonedTime } from "date-fns-tz"
import { useAccount } from "../context/AccountContext"
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId"

import "./UserStockStatus.css"
import {
  FaInfoCircle,
  FaChevronDown,
  FaChartBar,
  FaBatteryFull,
  FaBatteryHalf,
  FaBatteryEmpty,
  FaHistory,
  FaEdit,
  FaClipboardList,
  FaUserTie,
} from "react-icons/fa"
import { MdInfo } from "react-icons/md"

const UserStockStatus = ({ targetUserId }) => {
  const { currentAccount, loading: accountLoading } = useAccount()
  const { userId: effectiveUserId, isPartner, loading: partnerLoading } = useGetCurrentUserId()

  const [mainUserId, setMainUserId] = useState(null)
  const [codes, setCodes] = useState({})
  const [loading, setLoading] = useState(true)
  const [companyName, setCompanyName] = useState("")
  const [partnerName, setPartnerName] = useState("")
  const [activeTab, setActiveTab] = useState("report")

  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingValue, setPendingValue] = useState(null)
  const [amendLivePercentage, setAmendLivePercentage] = useState(null)

  const [showAssignModal, setShowAssignModal] = useState(false)
  const [assigningToSite, setAssigningToSite] = useState(null)
  const [partners, setPartners] = useState([])
  const [selectedPartner, setSelectedPartner] = useState("")
  const [sitePartnerAssignments, setSitePartnerAssignments] = useState({})

  // Report Tab
  const [selected, setSelected] = useState({ codeId: "", siteId: "", unit: "MT" })
  const [stockCapacity, setStockCapacity] = useState("")
  const [currentStock, setCurrentStock] = useState("")
  const [updating, setUpdating] = useState(false)
  const [livePercentage, setLivePercentage] = useState(null)
  const [showReportConfirm, setShowReportConfirm] = useState(false)

  // Amend Tab
  const [amendSelected, setAmendSelected] = useState({ codeId: "", siteId: "", unit: "MT" })
  const [amendCapacity, setAmendCapacity] = useState("")
  const [amendCurrent, setAmendCurrent] = useState("")
  const [amending, setAmending] = useState(false)
  const [showAmendConfirm, setShowAmendConfirm] = useState(false)

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [amendDropdownOpen, setAmendDropdownOpen] = useState(false)

  const gifUrl = "/Images/New notification.gif"
  const auth = getAuth()
  const db = getDatabase()

  // ──────────────────────────────
  // 1. Set mainUserId from prop (account switch)
  // ──────────────────────────────
  useEffect(() => {
    if (targetUserId) {
      setMainUserId(targetUserId)
      setLoading(false)
    }
  }, [targetUserId])

  // ──────────────────────────────
  // 2. Determine mainUserId + load partners & assignments (fallback path)
  // ──────────────────────────────
  useEffect(() => {
    if (accountLoading || partnerLoading || !currentAccount || !effectiveUserId || targetUserId) return

    const determineMainAndPartner = async () => {
      let mainId = effectiveUserId
      let partner = isPartner
      let pName = "Partner"

      if (isPartner) {
        const profilesSnap = await get(ref(db, "profileDashboard"))
        if (profilesSnap.exists()) {
          const profiles = profilesSnap.val()
          for (const uid in profiles) {
            if (profiles[uid].partners?.[auth.currentUser.uid]) {
              mainId = uid
              pName = profiles[uid].partners[auth.currentUser.uid].name || "Partner"
              break
            }
          }
        }
      }

      setMainUserId(mainId)
      setPartnerName(pName)
      if (partner) setActiveTab("amend")

      // Load partners list if main user
      if (!partner && mainId) {
        const partnersSnap = await get(ref(db, `profileDashboard/${mainId}/partners`))
        if (partnersSnap.exists()) {
          const list = Object.entries(partnersSnap.val()).map(([id, p]) => ({
            id,
            name: p.name || "Unknown",
          }))
          setPartners(list)
        }
      }
    }

    determineMainAndPartner()
  }, [currentAccount, effectiveUserId, isPartner, accountLoading, partnerLoading, targetUserId])
  // Force Amend tab as default for partners
useEffect(() => {
  if (isPartner) {
    setActiveTab("amend")
  } else {
    setActiveTab("report") // optional: default to report for main users
  }
}, [isPartner])

  // ──────────────────────────────
  // 3. Load company name
  // ──────────────────────────────
  useEffect(() => {
    if (!mainUserId) return
    const fetchName = async () => {
      const snap = await get(ref(db, `profileDashboard/${mainUserId}/company_name`))
      if (snap.exists()) setCompanyName(snap.val())
    }
    fetchName()
  }, [mainUserId])

  // ──────────────────────────────
  // 4. Real-time listeners: codes + assignments (PROPER CLEANUP!)
  // ──────────────────────────────
  useEffect(() => {
    if (!mainUserId) return

    const codesRef = ref(db, `new-code/${mainUserId}/codes`)
    const assignRef = ref(db, `site_partner_assignments/${mainUserId}`)

    // Initial fetch for assignments
    get(assignRef).then(snap => setSitePartnerAssignments(snap.val() || {}))

    const unsubCodes = onValue(codesRef, (snap) => {
      setCodes(snap.val() || {})
      setLoading(false)
    })

    const unsubAssign = onValue(assignRef, (snap) => {
      setSitePartnerAssignments(snap.val() || {})
    })

    return () => {
      unsubCodes()
      unsubAssign()
    }
  }, [mainUserId])
  // CRITICAL: Load partners list whenever mainUserId changes
  // Load partners from the MAIN USER (not the current targetUserId if it's a firm)
  useEffect(() => {
    if (isPartner || !mainUserId) {
      setPartners([])
      return
    }

    // We need the MAIN USER's partners — not the firm’s
    // So we must know who the main user is
    const loadMainUserPartners = async () => {
      // First: find the real main user (even if current target is a firm)
      const profilesSnap = await get(ref(db, "profileDashboard"))
      if (!profilesSnap.exists()) {
        setPartners([])
        return
      }

      const profiles = profilesSnap.val()
      let realMainUserId = null

      for (const uid in profiles) {
        const data = profiles[uid]
        if (data.firms && data.firms[mainUserId]) {
          // Current targetUserId (mainUserId) is a firm → parent is main user
          realMainUserId = uid
          break
        }
        if (uid === mainUserId && data.partners) {
          // Already viewing main user
          realMainUserId = uid
          break
        }
      }

      if (!realMainUserId) {
        setPartners([])
        return
      }

      const partnersRef = ref(db, `profileDashboard/${realMainUserId}/partners`)

      get(partnersRef).then(snap => {
        if (snap.exists()) {
          const list = Object.entries(snap.val()).map(([id, p]) => ({
            id,
            name: p.name || "Unknown Partner",
          }))
          setPartners(list)
        } else {
          setPartners([])
        }
      })

      const unsub = onValue(partnersRef, snap => {
        if (snap.exists()) {
          const list = Object.entries(snap.val()).map(([id, p]) => ({
            id,
            name: p.name || "Unknown Partner",
          }))
          setPartners(list)
        } else {
          setPartners([])
        }
      })

      return () => unsub()
    }

    loadMainUserPartners()
  }, [mainUserId, isPartner])

  // ──────────────────────────────
  // 5. Live percentage calculators
  // ──────────────────────────────
  useEffect(() => {
    if (stockCapacity && currentStock) {
      const cap = parseFloat(stockCapacity)
      const cur = parseFloat(currentStock)
      if (!isNaN(cap) && !isNaN(cur) && cap > 0) {
        setLivePercentage(((cur / cap) * 100).toFixed(1))
      } else {
        setLivePercentage(null)
      }
    } else {
      setLivePercentage(null)
    }
  }, [stockCapacity, currentStock])

  useEffect(() => {
    if (amendCapacity && amendCurrent) {
      const cap = parseFloat(amendCapacity)
      const cur = parseFloat(amendCurrent)
      if (!isNaN(cap) && !isNaN(cur) && cap > 0) {
        setAmendLivePercentage(((cur / cap) * 100).toFixed(1))
      } else {
        setAmendLivePercentage(null)
      }
    } else {
      setAmendLivePercentage(null)
    }
  }, [amendCapacity, amendCurrent])

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".stockstatus_dropdown_group")) {
        setIsDropdownOpen(false)
        setAmendDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Reset form when mainUserId changes
  useEffect(() => {
    setSelected({ codeId: "", siteId: "", unit: "MT" })
    setAmendSelected({ codeId: "", siteId: "", unit: "MT" })
    setStockCapacity("")
    setCurrentStock("")
    setAmendCapacity("")
    setAmendCurrent("")
    setLivePercentage(null)
    setAmendLivePercentage(null)
  }, [mainUserId])

  // ──────────────────────────────
  // Helper Functions
  // ──────────────────────────────
  const getTimestampMs = (ts) => (!ts ? 0 : new Date(ts).getTime())
  const getIndianTime = (ts) => (!ts ? "—" : format(toZonedTime(new Date(ts), "Asia/Kolkata"), "dd MMM yyyy, hh:mm a"))

  const getFillClass = (pct) => {
    if (!pct) return ""
    if (pct > 60) return "green"
    if (pct >= 30) return "yellow"
    return "red"
  }

  const getMessage = (pct) => {
    if (!pct) return { icon: <FaChartBar />, msg: "Enter values to see status" }
    if (pct > 60) return { icon: <FaBatteryFull />, msg: `${pct}% (Full)` }
    if (pct >= 30) return { icon: <FaBatteryHalf />, msg: `${pct}% (Medium)` }
    return { icon: <FaBatteryEmpty />, msg: `${pct}% (Low)` }
  }

  const formatFullAddress = (site) => {
    if (!site) return "Unknown Site"
    const base = site.address || "Site"
    const parts = [site.city, site.state, site.pincode].filter(Boolean)
    return parts.length ? `${base} — ${parts.join(", ")}` : base
  }

  const getFilteredSites = () => {
    if (!codes || !mainUserId) return []
    const allSites = Object.entries(codes).flatMap(([cId, c]) =>
      Object.entries(c.sites || {}).map(([sId, s]) => ({
        codeId: cId,
        siteId: sId,
        ...s,
      }))
    )

    if (isPartner) {
      return allSites.filter(site => {
        const key = `${site.codeId}_${site.siteId}`
        return sitePartnerAssignments[key]?.partnerId === auth.currentUser.uid
      })
    }
    return allSites
  }

  // ──────────────────────────────
  // Notifications & Actions
  // ──────────────────────────────
  const sendUserNotification = async (address, cap, cur, pct, status, action) => {
    if (!mainUserId || !isPartner) return
    const notifRef = ref(db, `user_notifications/StockStatus/${mainUserId}`)
    await push(notifRef, {
      codeId: amendSelected.codeId,
      title: `Partner Updated Stock`,
      message: `Your Partner *${partnerName}* has ${action} stock at *${address}* → Capacity ${cap}, Current ${cur} (${pct}% - ${status})`,
      gifUrl,
      timestamp: new Date().toISOString(),
      seen: false,
      updatedBy: partnerName,
      siteAddress: address,
      percentage: pct,
      status,
    })
  }

  const handleAssignPartner = async () => {
    if (!selectedPartner || !assigningToSite) return alert("Select a partner")

    try {
      const siteKey = `${assigningToSite.codeId}_${assigningToSite.siteId}`
      const assignRef = ref(db, `site_partner_assignments/${mainUserId}/${siteKey}`) // ← mainUserId = current firm or main

      await update(assignRef, {
        partnerId: selectedPartner,
        partnerName: partners.find(p => p.id === selectedPartner)?.name || "Unknown",
        siteAddress: formatFullAddress(assigningToSite),
        city: assigningToSite.city || null,
        state: assigningToSite.state || null,
        pincode: assigningToSite.pincode || null,
        assignedOn: new Date().toISOString(),
      })

      alert("Partner assigned successfully!")
      setShowAssignModal(false)
      setAssigningToSite(null)
      setSelectedPartner("")
    } catch (e) {
      console.error(e)
      alert("Failed to assign partner.")
    }
  }

  const handleReport = async () => {
    if (!stockCapacity || !currentStock) return alert("Fill both fields")
    const cap = parseFloat(stockCapacity), cur = parseFloat(currentStock)
    if (isNaN(cap) || isNaN(cur) || cap <= 0) return alert("Invalid values")

    const site = codes[selected.codeId]?.sites[selected.siteId]
    if (site?.stockCapacity) return alert("Already reported. Use 'Amend' tab.")

    setUpdating(true)
    try {
      const siteRef = ref(db, `new-code/${mainUserId}/codes/${selected.codeId}/sites/${selected.siteId}`)
      const pct = ((cur / cap) * 100).toFixed(2)
      const status = pct > 60 ? "High" : pct >= 30 ? "Medium" : "Low"

      await update(siteRef, {
        stockStatus: status,
        stockCapacity: cap,
        currentStock: cur,
        stockPercentage: pct,
        stockUnit: selected.unit,
        updatedOn: new Date().toISOString(),
        updatedBy: effectiveUserId,
      })

      if (status !== "High") {
        const notifRef = ref(db, `supplier_notifications/StockStatus/${mainUserId}`)
        await push(notifRef, {
          codeId: selected.codeId,
          title: `${status} Stock Alert`,
          message: `${companyName} — Stock at ${site.address} is *${status}*. Please contact the client to place a new order ASAP.`,
          gifUrl,
          timestamp: new Date().toISOString(),
          seen: false,
        })
      }

      alert("Stock reported!")
      setStockCapacity("")
      setCurrentStock("")
      setSelected({ codeId: "", siteId: "", unit: "MT" })
    } catch (e) {
      alert("Failed to report.")
    } finally {
      setUpdating(false)
    }
  }

  const handleAmend = async () => {
    if (!amendCapacity || !amendCurrent) return alert("Fill both fields")
    const cap = parseFloat(amendCapacity), cur = parseFloat(amendCurrent)
    if (isNaN(cap) || isNaN(cur) || cap <= 0) return alert("Invalid values")

    setAmending(true)
    try {
      const siteRef = ref(db, `new-code/${mainUserId}/codes/${amendSelected.codeId}/sites/${amendSelected.siteId}`)
      const site = codes[amendSelected.codeId]?.sites[amendSelected.siteId]
      const pct = ((cur / cap) * 100).toFixed(2)
      const status = pct > 60 ? "High" : pct >= 30 ? "Medium" : "Low"

      await update(siteRef, {
        stockStatus: status,
        stockCapacity: cap,
        currentStock: cur,
        stockPercentage: pct,
        stockUnit: amendSelected.unit,
        updatedOn: new Date().toISOString(),
        updatedBy: effectiveUserId,
        amended: true,
        previous: { currentStock: site.currentStock, stockCapacity: site.stockCapacity },
      })

      if (isPartner) await sendUserNotification(site.address, cap, cur, pct, status, "updated")

      if (status !== "High") {
        const notifRef = ref(db, `supplier_notifications/StockStatus/${mainUserId}`)
        await push(notifRef, {
          codeId: amendSelected.codeId,
          title: `${status} Stock`,
          message: `${companyName} — Stock at ${site.address} is *${status}*. Kindly contact the client for reorder.`,
          gifUrl,
          timestamp: new Date().toISOString(),
          seen: false,
        })
      }

      alert("Stock Updated Successfully.")
      setAmendCapacity("")
      setAmendCurrent("")
      setAmendSelected({ codeId: "", siteId: "", unit: "MT" })
    } catch (e) {
      console.error(e)
      alert("Failed to Update.")
    } finally {
      setAmending(false)
    }
  }

  const info = () => {
    alert(
      isPartner
        ? "Update Stock Here\n\nWhy? So your main user knows when to reorder and avoid running out.\n\nWhat you can do:\n• Only use 'Amend' tab to update stock\n• Your update goes to main user instantly\n• Use the slider for fast entry\n\nGoal: Never run out of material!"
        : "Manage Stock & Partners\n\nWhy? To prevent production stoppages by tracking stock in real time.\n\nYour Tools:\n• Report → Enter stock first time\n• Amend → Fix or update stock\n• History → See past updates\n• Assign → Give partners access to sites\n\nGoal: Always have enough stock, on time!"
    )
  }

  if (loading || accountLoading || partnerLoading || !mainUserId) {
    return <div className="stockstatus_loader">Loading...</div>
  }

  const filteredSites = getFilteredSites()
  return (
    <div className="stockstatus_page">
      <div className="stockstatus_card">
        <header className="stockstatus_header">
          <h1>
            Report Your Stock
            <img src="/Images/stock.png" className="stock-img-icon" />
          </h1>
          <MdInfo onClick={info} className="stockstatus_info_icon" />
        </header>

       <div className="stockstatus_tabs">
  {!isPartner && (
    <button
      className={activeTab === "report" ? "stockstatus_tab active" : "stockstatus_tab"}
      onClick={() => setActiveTab("report")}
    >
      <FaClipboardList /> Add Stock
      <MdInfo
        className="stockstatus_tab_info"
        onClick={(e) => {
          e.stopPropagation()
          alert(
            "Add Stock Tab\n\nUse this to report stock for a site for the FIRST TIME.\n\n• Select a site\n• Enter capacity and current stock\n• This creates the initial stock record"
          )
        }}
      />
    </button>
  )}

  <button
    className={activeTab === "amend" ? "stockstatus_tab active" : "stockstatus_tab"}
    onClick={() => setActiveTab("amend")}
  >
    <FaEdit /> Amend
    <MdInfo
      className="stockstatus_tab_info"
      onClick={(e) => {
        e.stopPropagation()
        alert(
          "Amend Tab\n\nUse this to UPDATE or CORRECT existing stock values.\n\n• Only appears after stock is reported once\n• You can change capacity or current stock\n• Fast slider for quick updates"
        )
      }}
    />
  </button>

  {!isPartner && (
    <button
      className={activeTab === "assign" ? "stockstatus_tab active" : "stockstatus_tab"}
      onClick={() => setActiveTab("assign")}
    >
      <FaUserTie /> Assign Partner
      <MdInfo
        className="stockstatus_tab_info"
        onClick={(e) => {
          e.stopPropagation()
          alert(
            "Assign Partner Tab\n\nUse this to give a partner permission to update stock for specific sites.\n\n• Only main users can assign\n• Partners will then see and update only their assigned sites"
          )
        }}
      />
    </button>
  )}
</div>

        {/* REPORT TAB */}
        {activeTab === "report" && !isPartner && (
          <div className="stockstatus_tab_pane">
            <div className="stockstatus_dropdown_group">
              <label>Select Site</label>
              <div
                className="stockstatus_dropdown"
                onClick={(e) => {
                  if (e.target.closest(".stockstatus_arrow")) return
                  setIsDropdownOpen((prev) => !prev)
                }}
              >
                <span>
                  {selected.codeId
                    ? formatFullAddress(codes[selected.codeId]?.sites?.[selected.siteId])
                    : "Choose site"
                  }
                </span>
                <FaChevronDown
                  className={isDropdownOpen ? "stockstatus_arrow open" : "stockstatus_arrow"}
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsDropdownOpen((prev) => !prev)
                  }}
                />
              </div>

              {isDropdownOpen && (
                <ul className="stockstatus_menu">
                  {Object.entries(codes || {}).map(([cId, c]) =>
                    Object.entries(c?.sites || {}).map(([sId, s]) => (
                      <li
                        key={`${cId}_${sId}`}
                        onClick={() => {
                          setSelected({ codeId: cId, siteId: sId, unit: s.stockUnit || "" })
                          setStockCapacity(s.stockCapacity || "")
                          setCurrentStock(s.currentStock || "")
                          setIsDropdownOpen(false)
                        }}
                      >
                        {formatFullAddress(s)}
                      </li>
                    )),
                  )}
                </ul>
              )}
            </div>

            {selected.siteId && (
              <>
                {/* Cylinder and Slider */}
                <div className="stockstatus_visual_row">
                  <div className="stockstatus_cylinder_container">
                    <div className="stockstatus_cylinder">
                      <div
                        className={`stockstatus_fill ${getFillClass(livePercentage)}`}
                        style={{ height: `${livePercentage || 0}%` }}
                      ></div>
                    </div>
                    <div className="stockstatus_msg">
                      {getMessage(livePercentage).icon} <span>{getMessage(livePercentage).msg}</span>
                    </div>
                  </div>

                  {/* SLIDER */}
                  <div className="stockstatus_slider_container">
                    <input
                      type="range"
                      min="0"
                      max={stockCapacity || 100}
                      value={currentStock || 0}
                      onChange={(e) => setCurrentStock(e.target.value)}
                      className="stockstatus_slider"
                      style={{
                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${livePercentage || 0}%, #e5e7eb ${livePercentage || 0}%, #e5e7eb 100%)`,
                      }}
                    />
                   <div className="stockstatus_slider_labels">
  <span className="stockstatus_slider_label_left"></span>
  
  <span className="stockstatus_slider_mid">
    {currentStock ? (
      <strong>{currentStock} {selected.unit}</strong>
    ) : (
      "—"
    )}
  </span>
  
  <span className="stockstatus_slider_label_right">
    {stockCapacity ? `${stockCapacity} ${selected.unit}` : "?"}
  </span>
</div>
                  </div>
                </div>

                {/* Inputs */}
                <div className="stockstatus_input_row">

                  {/* Stock Capacity */}
                  <div className="stockstatus_input_box">
                    <label className="stockstatus_label_with_dropdown">
                      Stock Capacity
                      <select
                        value={selected.unit}
                        onChange={(e) =>
                          setSelected((prev) => ({ ...prev, unit: e.target.value }))
                        }
                        className="stockstatus_label_dropdown"
                      >
                        <option value="MT">MT</option>
                        <option value="Bags">Bags</option>
                      </select>
                    </label>
                    <input
                      type="number"
                      value={stockCapacity}
                      onChange={(e) => setStockCapacity(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      placeholder="e.g. 100"
                    />
                  </div>

                  {/* Current Stock */}
                  <div className="stockstatus_input_box">
                    <label className="stockstatus_label_with_dropdown">
                      Current Stock
                      <select
                        value={selected.unit}
                        onChange={(e) =>
                          setSelected((prev) => ({ ...prev, unit: e.target.value }))
                        }
                        className="stockstatus_label_dropdown"
                      >
                        <option value="MT">MT</option>
                        <option value="Bags">Bags</option>
                      </select>
                    </label>
                    <input
                      type="number"
                      value={currentStock}
                      onChange={(e) => setCurrentStock(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      placeholder="e.g. 75"
                    />
                  </div>

                </div>

                {/* Button */}
                <div className="stockstatus_btn_container">
                  <button
                    onClick={() => setShowReportConfirm(true)}
                    disabled={updating || !stockCapacity || !currentStock}
                    className="stockstatus_btn_primary"
                  >
                    {updating ? "Reporting..." : "Update Stock Status"}
                  </button>
                </div>

                {/* Warning below */}
                {codes[selected.codeId]?.sites[selected.siteId]?.stockCapacity && (
                  <div className="stockstatus_warning_below">
                    <strong>Stock already reported.</strong> Use <strong>Amend</strong> tab to update.
                  </div>
                )}
              </>
            )}

            {!selected.siteId && <div className="stockstatus_empty_msg">Select a site to begin.</div>}

            {/* Report Confirmation Modal */}
            {showReportConfirm && (
              <div className="stockstatus_modal_overlay">
                <div className="stockstatus_modal_box">
                  <h3>Confirm Stock Report</h3>
                  <p>
                    Capacity: {stockCapacity} {selected.unit}<br />
                    Current: {currentStock} {selected.unit} ({livePercentage}%)
                  </p>
                  <div className="stockstatus_modal_buttons">
                    <button
                      className="confirm_btn"
                      onClick={async () => {
                        setShowReportConfirm(false)
                        await handleReport()
                      }}
                    >
                      Yes
                    </button>
                    <button
                      className="cancel_btn"
                      onClick={() => setShowReportConfirm(false)}
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}


        {/* AMEND TAB */}
        {activeTab === "amend" && (
          <div className="stockstatus_tab_pane">
            <div className="stockstatus_dropdown_group">
              <label>Site to Amend</label>
              <div
                className="stockstatus_dropdown"
                onClick={(e) => {
                  if (e.target.closest(".stockstatus_arrow")) return
                  setAmendDropdownOpen((prev) => !prev)
                }}
              >
                <span>
                  {amendSelected.codeId
                    ? formatFullAddress(codes[amendSelected.codeId]?.sites?.[amendSelected.siteId])
                    : "Select Site to Amend"
                  }
                </span>
                <FaChevronDown
                  className={amendDropdownOpen ? "stockstatus_arrow open" : "stockstatus_arrow"}
                  onClick={(e) => {
                    e.stopPropagation()
                    setAmendDropdownOpen((prev) => !prev)
                  }}
                />
              </div>

              {amendDropdownOpen && (
                <ul className="stockstatus_menu">
                  {filteredSites
                    .filter((site) => site?.stockCapacity)
                    .map((site) => (
                      <li
                        key={`${site.codeId}_${site.siteId}`}
                        onClick={() => {
                          setAmendSelected({
                            codeId: site.codeId,
                            siteId: site.siteId,
                            unit: site.stockUnit || "MT"
                          })
                          setAmendCapacity(site.stockCapacity || "")
                          setAmendCurrent(site.currentStock || "")
                          setAmendDropdownOpen(false)
                        }}
                      >
                        {formatFullAddress(site)}
                      </li>
                    ))}
                  {filteredSites.filter(site => site && site.stockCapacity).length === 0 && (
                    <li className="stockstatus_no_data">No stock reported yet</li>
                  )}
                </ul>
              )}

            </div>

            {amendSelected.siteId && (
              <>
                {/* Cylinder and Slider */}
                <div className="stockstatus_visual_row">
                  <div className="stockstatus_cylinder_container">
                    <div className="stockstatus_cylinder">
                      <div
                        className={`stockstatus_fill ${getFillClass(amendLivePercentage)}`}
                        style={{ height: `${amendLivePercentage || 0}%` }}
                      ></div>
                    </div>
                    <div className="stockstatus_msg">
                      {getMessage(amendLivePercentage).icon} <span>{getMessage(amendLivePercentage).msg}</span>
                    </div>
                  </div>

                  {/* SLIDER */}
                  <div className="stockstatus_slider_container">
                    <input
                      type="range"
                      min="0"
                      max={amendCapacity || 100}
                      value={amendCurrent || 0}
                      onChange={(e) => setAmendCurrent(e.target.value)}
                      className="stockstatus_slider"
                      style={{
                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${amendLivePercentage || 0}%, #e5e7eb ${amendLivePercentage || 0}%, #e5e7eb 100%)`,
                      }}
                    />
                    {/* SLIDER LABELS – AMEND */}
                  <div className="stockstatus_slider_labels">
  <span className="stockstatus_slider_label_left"></span>
  
  <span className="stockstatus_slider_mid">
    {amendCurrent ? (
      <strong>{amendCurrent} {amendSelected.unit}</strong>
    ) : (
      "—"
    )}
  </span>
  
  <span className="stockstatus_slider_label_right">
    {amendCapacity ? `${amendCapacity} ${amendSelected.unit}` : "?"}
  </span>
</div>
                  </div>
                </div>

                {/* Inputs */}
                <div className="stockstatus_input_row">

                  {/* Corrected Capacity */}
                  <div className="stockstatus_input_box">
                    <label className="stockstatus_label_with_dropdown">
                      Current Capacity
                      <select
                        value={amendSelected.unit}
                        onChange={(e) =>
                          setAmendSelected((prev) => ({ ...prev, unit: e.target.value }))
                        }
                        className="stockstatus_label_dropdown"
                      >
                        <option value="MT">MT</option>
                        <option value="Bags">Bags</option>
                      </select>
                    </label>

                    <input
                      type="number"
                      value={pendingValue !== null ? pendingValue : amendCapacity}
                      onChange={(e) => setPendingValue(e.target.value)}
                      onBlur={() => {
                        if (
                          pendingValue !== null &&
                          pendingValue !== amendCapacity.toString()
                        ) {
                          setShowConfirmModal(true)
                        }
                      }}
                      onWheel={(e) => e.target.blur()}
                      placeholder={`e.g. ${amendCapacity}`}
                    />
                  </div>

                  {/* Corrected Stock */}
                  <div className="stockstatus_input_box">
                    <label className="stockstatus_label_with_dropdown">
                      Current Stock
                      <select
                        value={amendSelected.unit}
                        onChange={(e) =>
                          setAmendSelected((prev) => ({ ...prev, unit: e.target.value }))
                        }
                        className="stockstatus_label_dropdown"
                      >
                        <option value="MT">MT</option>
                        <option value="Bags">Bags</option>
                      </select>
                    </label>

                    <input
                      type="number"
                      value={amendCurrent}
                      onChange={(e) => setAmendCurrent(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      placeholder={`e.g. ${amendCurrent}`}
                    />
                  </div>

                  {/* Confirmation Modal for Capacity */}
                  {showConfirmModal && (
                    <div className="stockstatus_modal_overlay">
                      <div className="stockstatus_modal_box">
                        <h3>Confirm Update</h3>
                        <p>
                          Do you want to update your stock capacity to {pendingValue}{" "}
                          {amendSelected.unit}?
                        </p>
                        <div className="stockstatus_modal_buttons">
                          <button
                            className="confirm_btn"
                            onClick={() => {
                              setAmendCapacity(pendingValue)
                              setPendingValue(null)
                              setShowConfirmModal(false)
                            }}
                          >
                            Yes
                          </button>
                          <button
                            className="cancel_btn"
                            onClick={() => {
                              setPendingValue(null)
                              setShowConfirmModal(false)
                            }}
                          >
                            No
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Button */}
                <div className="stockstatus_btn_container">
                  <button
                    onClick={() => setShowAmendConfirm(true)}
                    disabled={amending || !amendCapacity || !amendCurrent}
                    className="stockstatus_btn_primary"
                  >
                    {amending ? "Updating..." : "Update"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "assign" && !isPartner && (
          <div className="stockstatus_tab_pane">
            <div className="stockstatus_assign_container">
              <h3>Assign Partner to Sites</h3>
              <p className="text-center">Select sites and assign a partner to manage them</p>

              {filteredSites.filter(s => s.stockCapacity).length === 0 ? (
                <p className="stockstatus_empty_msg">
                  No sites with reported stock yet. Report stock first to assign partners.
                </p>
              ) : (
                <div className="stockstatus_sites_list">
                  {filteredSites
                    .filter((s) => s.stockCapacity)  // ← THIS LINE ADDED
                    .map((s, idx) => {
                      const siteKey = `${s.codeId}_${s.siteId}`
                      const assignedPartner = sitePartnerAssignments[siteKey]
                      return (
                        <div key={idx} className="stockstatus_site_card">
                          <div className="stockstatus_site_info">
                            <p className="stockstatus_site_full">
                              {formatFullAddress(s)}
                            </p>
                            {assignedPartner && (
                              <p className="stockstatus_assigned">
                                Assigned to: <strong>{assignedPartner.partnerName}</strong>
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setAssigningToSite({ ...s, codeId: s.codeId, siteId: s.siteId })
                              setSelectedPartner(assignedPartner?.partnerId || "")
                              setShowAssignModal(true)
                            }}
                            className="stockstatus_assign_btn"
                          >
                            <FaUserTie /> {assignedPartner ? "Change Partner" : "Assign Partner"}
                          </button>
                        </div>
                      )
                    })}
                </div>
              )}
            </div>

            {showAssignModal && (
              <div className="stockstatus_modal_overlay">
                <div className="stockstatus_modal_box stockstatus_assignment_modal">
                  <h3>Assign Partner to Site</h3>
                  <p className="stockstatus_modal_site">
                    <strong>Site:</strong> {assigningToSite ? formatFullAddress(assigningToSite) : ""}
                  </p>

                  <div className="stockstatus_modal_form">
                    <label>Select Partner:</label>
                    <select
                      value={selectedPartner}
                      onChange={(e) => setSelectedPartner(e.target.value)}
                      className="stockstatus_modal_select"
                    >
                      <option value="">-- Choose Partner --</option>
                      {partners.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="stockstatus_modal_buttons">
                    <button className="confirm_btn" onClick={handleAssignPartner} disabled={!selectedPartner}>
                      Assign
                    </button>
                    <button
                      className="cancel_btn"
                      onClick={() => {
                        setShowAssignModal(false)
                        setAssigningToSite(null)
                        setSelectedPartner("")
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Amend Confirmation Modal */}
        {showAmendConfirm && (
          <div className="stockstatus_modal_overlay">
            <div className="stockstatus_modal_box">
              <h3>Confirm Stock Update</h3>
              <p>
                Capacity: {amendCapacity} {amendSelected.unit}<br />
                Current: {amendCurrent} {amendSelected.unit} ({amendLivePercentage}%)
              </p>
              <div className="stockstatus_modal_buttons">
                <button
                  className="confirm_btn"
                  onClick={async () => {
                    setShowAmendConfirm(false)
                    await handleAmend()
                  }}
                >
                  Yes
                </button>
                <button
                  className="cancel_btn"
                  onClick={() => setShowAmendConfirm(false)}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default UserStockStatus